/**
 * Подписка на Web Push.
 *
 * Здесь сосредоточены все особенности платформ, из-за которых уведомления
 * «не работают», хотя код вроде бы верный:
 *
 *   iOS 16.4+   PushManager существует только в PWA, добавленном на домашний
 *               экран. В обычной вкладке Safari его просто нет — не «нет
 *               разрешения», а нет самого API. Поэтому кнопку «включить
 *               уведомления» на iOS показывать бессмысленно до установки.
 *
 *   везде       Notification.requestPermission() обязан вызываться из
 *               обработчика реального касания или клика. Из useEffect на
 *               монтировании браузер молча откажет.
 *
 *   везде       Подписка привязана к паре ключей VAPID сервера. Сменишь ключи —
 *               все подписки станут недействительными и уведомления пропадут
 *               до переподписки.
 */
import { api } from './api.js';
import { fromBase64Url } from './bytes.js';

export type PushState =
  | 'unsupported' // браузер не умеет Web Push вовсе
  | 'needs-install' // iOS: нужно добавить на домашний экран
  | 'prompt' // можно спрашивать разрешение
  | 'granted'
  | 'denied';

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS с «десктопным» User-Agent отличается наличием тач-точек.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Запущено ли приложение как установленное PWA, а не во вкладке. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function pushState(): PushState {
  if (!('serviceWorker' in navigator)) return 'unsupported';

  if (!('PushManager' in window)) {
    // На iOS отсутствие PushManager означает «ещё не установлено», а не
    // «не поддерживается»: после добавления на домашний экран он появится.
    return isIOS() ? 'needs-install' : 'unsupported';
  }
  if (!('Notification' in window)) return 'unsupported';

  switch (Notification.permission) {
    case 'granted':
      return 'granted';
    case 'denied':
      return 'denied';
    default:
      return 'prompt';
  }
}

/**
 * Запрашивает разрешение и оформляет подписку.
 * Вызывать строго из обработчика пользовательского действия.
 */
export async function enablePush(): Promise<{ ok: boolean; state: PushState; reason?: string }> {
  const state = pushState();
  if (state === 'unsupported' || state === 'needs-install' || state === 'denied') {
    return { ok: false, state, reason: describeRefusal(state) };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    /*
     * «default» — это не отказ пользователя, а отсутствие ответа: окно
     * с вопросом даже не показали. Состояние при этом остаётся прежним,
     * кнопка перерисовывается той же самой, и нажатие выглядит как
     * провалившееся в пустоту. Поэтому причину обязательно называем.
     *
     * В приложении на Android это почти всегда значит, что у самого
     * приложения нет системного разрешения на уведомления: объявить
     * POST_NOTIFICATIONS в манифесте мало, с Android 13 его ещё и
     * запрашивают у пользователя.
     */
    const refused: PushState = permission === 'denied' ? 'denied' : 'prompt';
    return { ok: false, state: refused, reason: describeRefusal(refused) };
  }

  const registration = await navigator.serviceWorker.ready;
  const { publicKey } = await api.pushKey();

  let subscription = await registration.pushManager.getSubscription();

  // Подписка могла остаться от прежней пары VAPID — такая уже не работает,
  // и сервер на неё будет получать отказы. Проверяем и переподписываемся.
  if (subscription && !sameApplicationServerKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      // Обязательно true: браузеры требуют, чтобы каждый пуш приводил
      // к видимому уведомлению. «Тихие» пуши для синхронизации запрещены.
      userVisibleOnly: true,
      applicationServerKey: fromBase64Url(publicKey) as BufferSource,
    });
  }

  await api.pushSubscribe(subscription.toJSON());
  return { ok: true, state: 'granted' };
}

/** Почему включить не получилось — текстом, который можно показать. */
export function describeRefusal(state: PushState): string {
  switch (state) {
    case 'unsupported':
      return 'Этот браузер не умеет push-уведомления.';
    case 'needs-install':
      return 'Сначала добавь приложение на домашний экран и запусти его с иконки.';
    case 'denied':
      return isAndroidApp()
        ? 'Уведомления запрещены. Включи их в настройках Android: Настройки → Приложения → Mess → Уведомления.'
        : 'Уведомления запрещены для этого сайта. Разреши их в настройках браузера — в адресной строке значок замка, дальше «Уведомления».';
    default:
      return isAndroidApp()
        ? 'Система не показала запрос разрешения. Проверь: Настройки → Приложения → Mess → Уведомления.'
        : 'Браузер не показал запрос разрешения. Попробуй ещё раз или проверь настройки уведомлений для этого сайта.';
  }
}

/**
 * Запущено ли из приложения-обёртки, а не из вкладки браузера.
 *
 * Различать важно ради подсказки: в обёртке разрешение живёт в настройках
 * Android и совет «посмотри значок замка в адресной строке» бесполезен —
 * адресной строки там нет.
 */
function isAndroidApp(): boolean {
  return /Android/.test(navigator.userAgent) && isStandalone();
}

function sameApplicationServerKey(subscription: PushSubscription, publicKey: string): boolean {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;

  const expected = fromBase64Url(publicKey);
  const actual = new Uint8Array(current);
  return (
    actual.length === expected.length && actual.every((byte, index) => byte === expected[index])
  );
}

export async function disablePush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await api.pushUnsubscribe(subscription.endpoint).catch(() => undefined);
  await subscription.unsubscribe();
}

/** Короткая подсказка под конкретную платформу — показывается в настройках. */
export function installHint(): string | null {
  if (!isIOS() || isStandalone()) return null;
  return 'На iPhone и iPad уведомления работают только у приложения, добавленного на домашний экран. Открой меню «Поделиться» в Safari и выбери «На экран „Домой“», затем запусти приложение с иконки.';
}
